import { defineStore } from "pinia";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import type { PersistedWorkspaceTab, WorkspaceTab, WorkspaceTabsSnapshot } from "@/type/workspaceTabs";
import {
  WORKSPACE_TAB_MAX,
  isWorkspaceTabRoute,
  workspaceTabDescriptor,
} from "@/config/workspaceTabs";

/**
 * Open-record tabs. See `docs/WorkspaceTabs.README.md`.
 *
 * A Pinia store rather than a module-scope composable: the latter is a singleton
 * BY ACCIDENT, which is the bug already present in `useRatingsAnalytics.ts:75-83`.
 * A store is a singleton by contract and gets devtools history, which matters a lot
 * when debugging tab lifecycle.
 */

const STORAGE_KEY = "alkokh_workspace_tabs";

function readSnapshot(): WorkspaceTabsSnapshot | null {
  if (typeof window === "undefined")
    return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw)
      return null;

    const parsed = JSON.parse(raw) as WorkspaceTabsSnapshot | null;
    if (!parsed || !Array.isArray(parsed.tabs))
      return null;

    // Validate on read: a stored tab whose route no longer exists (renamed page,
    // revoked permission) must be dropped rather than rendered as a dead tab.
    const tabs: PersistedWorkspaceTab[] = [];

    parsed.tabs.forEach((tab) => {
      const id = String(tab?.id ?? "").trim();
      const routeName = String(tab?.routeName ?? "").trim();

      if (!id || !isWorkspaceTabRoute(routeName) || tabs.length >= WORKSPACE_TAB_MAX)
        return;

      const entry: PersistedWorkspaceTab = {
        id,
        routeName,
        title: String(tab?.title ?? ""),
        icon: String(tab?.icon ?? ""),
        pinned: tab?.pinned === true,
      };

      if (tab?.titleKey)
        entry.titleKey = String(tab.titleKey);

      tabs.push(entry);
    });

    return { tabs, activeId: String(parsed.activeId ?? "") };
  }
  catch {
    return null;
  }
}

function persist(snapshot: WorkspaceTabsSnapshot) {
  if (typeof window === "undefined")
    return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  }
  catch {
    // Quota or privacy mode — tabs still work for this session.
  }
}

interface WorkspaceTabsState {
  tabs: WorkspaceTab[];
  activeId: string;
  /** Raised when eviction is blocked so the strip can surface one toast. */
  lastBlockedReason: "" | "max";
}

export const useWorkspaceTabsStore = defineStore("workspace-tabs", {
  state: (): WorkspaceTabsState => ({
    tabs: [],
    activeId: "",
    lastBlockedReason: "",
  }),

  getters: {
    /** Pinned first, otherwise insertion order. */
    orderedTabs(state): WorkspaceTab[] {
      return [
        ...state.tabs.filter(tab => tab.pinned),
        ...state.tabs.filter(tab => !tab.pinned),
      ];
    },
    activeTab(state): WorkspaceTab | null {
      return state.tabs.find(tab => tab.id === state.activeId) ?? null;
    },
    hasTabs(state): boolean {
      return state.tabs.length > 0;
    },
  },

  actions: {
    isTabbable(route: Pick<RouteLocationNormalizedLoaded, "name">) {
      return isWorkspaceTabRoute(route.name);
    },

    find(id: string) {
      return this.tabs.find(tab => tab.id === id) ?? null;
    },

    /**
     * Register a visit. Called from `router.afterEach`, so it must be cheap and
     * must never navigate — the router is mid-flight.
     *
     * Returns the tab id when the route is tabbable, otherwise "".
     */
    registerVisit(route: RouteLocationNormalizedLoaded, title: string) {
      if (!this.isTabbable(route))
        return "";

      const id = route.fullPath;
      const descriptor = workspaceTabDescriptor(route.name);
      const existing = this.find(id);

      if (existing) {
        existing.lastActiveAt = Date.now();
        if (title)
          existing.title = title;

        this.activate(id, { navigate: false });

        return id;
      }

      // A page that rewrote its own query (sales invoice `?mode=edit`) is the SAME
      // document. Rewrite the existing tab's id in place instead of opening a
      // second tab for it.
      const sameDocument = this.tabs.find(tab =>
        tab.routeName === String(route.name ?? "")
        && tab.id !== id
        && sameParams(tab.id, id),
      );

      if (sameDocument) {
        sameDocument.id = id;
        sameDocument.lastActiveAt = Date.now();
        if (title)
          sameDocument.title = title;

        this.activeId = id;
        this.save();

        return id;
      }

      if (this.tabs.length >= WORKSPACE_TAB_MAX && !this.evictOne()) {
        // Every tab is pinned or dirty — refuse rather than discard unsaved work.
        this.lastBlockedReason = "max";

        return "";
      }

      this.tabs.push({
        id,
        routeName: String(route.name ?? ""),
        title,
        icon: descriptor?.icon ?? "tabler-file",
        pinned: false,
        dirty: false,
        scrollY: 0,
        lastActiveAt: Date.now(),
      });

      this.activeId = id;
      this.save();

      return id;
    },

    activate(id: string, options: { navigate?: boolean } = {}) {
      const tab = this.find(id);
      if (!tab)
        return;

      tab.lastActiveAt = Date.now();
      this.activeId = id;
      this.save();

      // Navigation is the caller's job (the strip); afterEach must not re-enter
      // the router.
      void options;
    },

    setDirty(id: string, dirty: boolean) {
      const tab = this.find(id);
      if (tab)
        tab.dirty = dirty;
    },

    setScroll(id: string, scrollY: number) {
      const tab = this.find(id);
      if (tab)
        tab.scrollY = scrollY;
    },

    togglePinned(id: string) {
      const tab = this.find(id);
      if (!tab)
        return;

      tab.pinned = !tab.pinned;
      this.save();
    },

    /**
     * Drop the least-recently-active tab that is neither pinned nor dirty.
     * Returns false when nothing may be evicted.
     */
    evictOne() {
      const candidates = this.tabs
        .filter(tab => !tab.pinned && !tab.dirty && tab.id !== this.activeId)
        .sort((a, b) => a.lastActiveAt - b.lastActiveAt);

      const victim = candidates[0];
      if (!victim)
        return false;

      this.close(victim.id);

      return true;
    },

    /**
     * Remove a tab. Returns the id that should become active, or "" when the
     * caller should fall back to the default route.
     */
    close(id: string) {
      const index = this.tabs.findIndex(tab => tab.id === id);
      if (index === -1)
        return this.activeId;

      const wasActive = this.activeId === id;
      this.tabs.splice(index, 1);

      if (!wasActive) {
        this.save();

        return this.activeId;
      }

      // Chrome's rule: prefer the neighbour toward the inline end, else the one
      // before it.
      const next = this.tabs[index] ?? this.tabs[index - 1] ?? null;
      this.activeId = next?.id ?? "";
      this.save();

      return this.activeId;
    },

    closeOthers(id: string) {
      this.tabs = this.tabs.filter(tab => tab.id === id || tab.pinned || tab.dirty);
      this.activeId = id;
      this.save();
    },

    closeAll() {
      this.tabs = this.tabs.filter(tab => tab.pinned || tab.dirty);
      this.activeId = this.tabs[0]?.id ?? "";
      this.save();
    },

    reorder(fromId: string, toId: string) {
      const from = this.tabs.findIndex(tab => tab.id === fromId);
      const to = this.tabs.findIndex(tab => tab.id === toId);
      if (from === -1 || to === -1 || from === to)
        return;

      const [moved] = this.tabs.splice(from, 1);
      this.tabs.splice(to, 0, moved);
      this.save();
    },

    /** Turning the feature off must not strand cached pages. */
    reset() {
      this.tabs = [];
      this.activeId = "";
      this.lastBlockedReason = "";
      persist({ tabs: [], activeId: "" });
    },

    save() {
      persist({
        tabs: this.tabs.map(tab => ({
          id: tab.id,
          routeName: tab.routeName,
          title: tab.title,
          titleKey: tab.titleKey,
          icon: tab.icon,
          pinned: tab.pinned,
        })),
        activeId: this.activeId,
      });
    },

    loadFromStorage() {
      const snapshot = readSnapshot();
      if (!snapshot)
        return;

      const now = Date.now();

      this.tabs = snapshot.tabs.map(tab => ({
        ...tab,
        dirty: false,
        scrollY: 0,
        lastActiveAt: now,
      }));

      this.activeId = this.find(snapshot.activeId) ? snapshot.activeId : "";
    },
  },
});

/**
 * Two full paths address the same document when their path segment matches; only
 * the query differs. Used to detect a self-rewrite (`?mode=edit`) rather than a
 * genuinely new record.
 */
function sameParams(a: string, b: string) {
  return a.split("?")[0] === b.split("?")[0];
}
